// ReferralLeadPanel — the PUBLIC ?ref= landing form (account-area redesign
// Task 20 slice 2, migration 147).
//
// A share link lands its recipient (the referred store's contact) on the
// marketplace mount as {mount}?ref=<token>; the router routes that here
// BEFORE any auth gate (the visitor is anonymous by definition — the route
// carries needsAuth=false). Submission POSTs /api/v1/marketplace/referral-
// leads: the token plus the SAME seven-required contact fields the
// authenticated referral form uses (store name, contact first/last name,
// phone, email, city, state — the server's 422 names exactly these).
// prior_discussion is deliberately ABSENT from the public form: it is the
// REFERRER's claim of prior contact, not the store's.
//
// reCAPTCHA (the existing OTP precedent): the site key comes from the mount's
// RUNTIME data-config, threaded through MarketplaceEmbedProps. With a key the
// token is acquired on submit (executeRecaptchaToken, the SignInPanel
// conditional); without one the flow sends recaptcha_token:"" and the SERVER
// decides (keyed=enforce, keyless=bypass — the customer_auth_service posture
// this route shares).
//
// The referrer-side refusals are ONE indistinguishable 404 envelope
// ("Referral link not found") — unknown token, rotated token, an ineligible
// referrer (OWASP A01) — surfaced verbatim via ApiError.message. (A
// self-referral is not a refusal since TD-716: the server answers the same
// 200 and records it privately.)
// Blank optionals are OMITTED from the body (the binding wire constraint;
// referral-api.ts enforces it).
//
// COUNSEL-REVIEW: every static string in this file is program copy — the
// complete block is collected verbatim in the task report.
//
// Inline styles only — no Tailwind (embed convention). Every <button> is
// explicitly type="button"/type="submit" (project rule 73).

import { useState, useCallback, type FormEvent } from "react";
import {
  useEmbedTokens,
  EmbedCard,
  EmbedHeading,
  EmbedText,
  EmbedButton,
  EmbedBanner,
  EmbedField,
  EmbedInput,
} from "../../embed-shared/primitives";
import { ApiError } from "../../embed-shared/api-client";
import { executeRecaptchaToken } from "../../embed-shared/recaptcha";
import { submitReferralLead } from "./referral-api";

export interface ReferralLeadPanelProps {
  /** The ?ref= token this landing was opened with (verbatim into the submit
   *  body — the server resolves it, revokes included). */
  refToken: string;
  /** reCAPTCHA v3 site key from the mount's runtime data-config. Empty/
   *  undefined sends an empty token (the keyless posture). */
  recaptchaSiteKey?: string;
}

interface LeadFields {
  store_name: string;
  contact_first_name: string;
  contact_last_name: string;
  contact_phone: string;
  contact_email: string;
  description: string;
  street: string;
  city: string;
  state: string;
}

const EMPTY_LEAD: LeadFields = {
  store_name: "",
  contact_first_name: "",
  contact_last_name: "",
  contact_phone: "",
  contact_email: "",
  description: "",
  street: "",
  city: "",
  state: "",
};

export function ReferralLeadPanel({ refToken, recaptchaSiteKey }: ReferralLeadPanelProps) {
  const t = useEmbedTokens();
  const [fields, setFields] = useState<LeadFields>(EMPTY_LEAD);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const updateField = useCallback((key: keyof LeadFields, value: string) => {
    setFields((prev) => ({ ...prev, [key]: value }));
    setFieldError(null);
  }, []);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      // Every missing required field at once (the server's 422 names them
      // all; the form mirrors that instead of dribbling one per click).
      const required: [keyof LeadFields, string][] = [
        ["store_name", "Store name"],
        ["contact_first_name", "Contact first name"],
        ["contact_last_name", "Contact last name"],
        ["contact_phone", "Contact phone"],
        ["contact_email", "Contact email"],
        ["city", "City"],
        ["state", "State"],
      ];
      const missing = required.filter(([key]) => !fields[key].trim()).map(([, label]) => label);
      if (missing.length > 0) {
        setFieldError(`Please fill in: ${missing.join(", ")}.`);
        return;
      }
      setSubmitting(true);
      setFormError(null);
      try {
        // The OTP flow's conditional: with a site key the v3 token is
        // acquired at submit time; without one the body carries "" and the
        // server's keyless posture decides.
        const recaptchaToken = recaptchaSiteKey
          ? await executeRecaptchaToken(recaptchaSiteKey, "marketplace_referral_lead")
          : "";
        // Blank optionals are OMITTED from the body — never "" (the wire
        // constraint; submitReferralLead enforces the shape).
        await submitReferralLead({
          token: refToken,
          store_name: fields.store_name,
          contact_first_name: fields.contact_first_name,
          contact_last_name: fields.contact_last_name,
          contact_phone: fields.contact_phone,
          contact_email: fields.contact_email,
          city: fields.city,
          state: fields.state,
          recaptcha_token: recaptchaToken,
          ...(fields.description.trim() ? { description: fields.description } : {}),
          ...(fields.street.trim() ? { street: fields.street } : {}),
        });
        setSubmitted(true);
      } catch (err) {
        // The server's envelope messages are the outcomes, not errors to
        // reinterpret: the uniform 404's "Referral link not found", the 403's
        // reCAPTCHA refusal, the 422's field list. Only a transport failure
        // falls back to generic copy.
        setFormError(err instanceof ApiError ? err.message : "We couldn't submit your details. Please try again.");
      } finally {
        setSubmitting(false);
      }
    },
    [fields, recaptchaSiteKey, refToken],
  );

  return (
    <div data-testid="referral-lead-panel" style={{ maxWidth: 640 }}>
      <EmbedCard>
        <EmbedHeading level={2}>Tell us about your store</EmbedHeading>
        <div style={{ marginTop: t.spacing.xs }}>
          <EmbedText tone="secondary">
            Someone who knows Aldelo suggested we connect. Share your details
            and our team may reach out.
          </EmbedText>
        </div>

        {submitted ? (
          <div style={{ marginTop: t.spacing.md }} role="status">
            <EmbedBanner tone="success">
              Thank you — we received your details. Aldelo&apos;s team may reach out.
            </EmbedBanner>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            {formError && (
              <div style={{ marginTop: t.spacing.md }}>
                <EmbedBanner tone="error">{formError}</EmbedBanner>
              </div>
            )}
            {fieldError && (
              <div style={{ marginTop: t.spacing.md }}>
                <EmbedBanner tone="error">{fieldError}</EmbedBanner>
              </div>
            )}

            <div style={{ marginTop: t.spacing.md }}>
              <EmbedField label="Store name" htmlFor="lead-store-name" required>
                <EmbedInput
                  id="lead-store-name"
                  type="text"
                  value={fields.store_name}
                  onChange={(e) => updateField("store_name", e.target.value)}
                  aria-required="true"
                />
              </EmbedField>

              <EmbedField label="Contact first name" htmlFor="lead-first" required>
                <EmbedInput
                  id="lead-first"
                  type="text"
                  value={fields.contact_first_name}
                  onChange={(e) => updateField("contact_first_name", e.target.value)}
                  aria-required="true"
                />
              </EmbedField>

              <EmbedField label="Contact last name" htmlFor="lead-last" required>
                <EmbedInput
                  id="lead-last"
                  type="text"
                  value={fields.contact_last_name}
                  onChange={(e) => updateField("contact_last_name", e.target.value)}
                  aria-required="true"
                />
              </EmbedField>

              <EmbedField label="Contact phone" htmlFor="lead-phone" required>
                <EmbedInput
                  id="lead-phone"
                  type="tel"
                  value={fields.contact_phone}
                  onChange={(e) => updateField("contact_phone", e.target.value)}
                  aria-required="true"
                />
              </EmbedField>

              <EmbedField label="Contact email" htmlFor="lead-email" required>
                <EmbedInput
                  id="lead-email"
                  type="email"
                  value={fields.contact_email}
                  onChange={(e) => updateField("contact_email", e.target.value)}
                  aria-required="true"
                />
              </EmbedField>

              <EmbedField label="City" htmlFor="lead-city" required>
                <EmbedInput
                  id="lead-city"
                  name="city"
                  autoComplete="address-level2"
                  type="text"
                  value={fields.city}
                  onChange={(e) => updateField("city", e.target.value)}
                  aria-required="true"
                />
              </EmbedField>

              <EmbedField label="State" htmlFor="lead-state" required>
                <EmbedInput
                  id="lead-state"
                  name="state"
                  autoComplete="address-level1"
                  type="text"
                  value={fields.state}
                  onChange={(e) => updateField("state", e.target.value)}
                  aria-required="true"
                />
              </EmbedField>

              <EmbedHeading level={3}>More details (optional)</EmbedHeading>
              <EmbedField label="Description" htmlFor="lead-description" help="Optional — anything useful for our team reaching out.">
                <EmbedInput
                  id="lead-description"
                  type="text"
                  value={fields.description}
                  onChange={(e) => updateField("description", e.target.value)}
                />
              </EmbedField>

              <EmbedField label="Street" htmlFor="lead-street" help="Optional.">
                <EmbedInput
                  id="lead-street"
                  name="street"
                  autoComplete="street-address"
                  type="text"
                  value={fields.street}
                  onChange={(e) => updateField("street", e.target.value)}
                />
              </EmbedField>
            </div>

            <div style={{ marginTop: t.spacing.md }}>
              <EmbedButton type="submit" variant="primary" disabled={submitting} loading={submitting}>
                {submitting ? "Submitting..." : "Submit"}
              </EmbedButton>
            </div>
          </form>
        )}
      </EmbedCard>
    </div>
  );
}
